// @ts-nocheck

import React, { useState, useRef, useMemo, useLayoutEffect } from 'react';
import styled, { css, keyframes } from 'styled-components';

import { Target } from '../../../../client';
import { MPCardColorSvg, MPCardStationSvg, MPCardWaterSvg, MPCardElectricitySvg } from '../../assets/svg';
import MPCardPendingBorderSvgAsComponent from '../../assets/svg/asComponent/MPCardPendingBorderSvgAsComponent';
import { MPColors } from '../../constants/colors';
import { MP_CARD_HALO_DURATION } from '../../constants/ui';
import { MPCardTypes, type MPCardType, type MPCardStatus, MPCardStatuses } from '../../types/cards';
import { type MPFamilyId } from '../../types/families';

const FONT_SIZE_BASE = 10;

const haloAnim = keyframes`
  0% {
    opacity: 0.2;
  }

  50% {
    opacity: 1;
  }

  100% {
    opacity: 0.2;
  }
`;

const StyleReceiver = styled.div``;

const Wrapper = styled.div`
  position: relative;
  line-height: 0.8em;
  width: 100%;
  height: 100%;

  user-select: none;

  box-shadow: 0 0 0.6em 0 rgba(0, 0, 0, 0.5);

  &::after {
    content: '';
    bottom: 0;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
  }

  ${({ $isClickable }) =>
    $isClickable
      ? css`
          cursor: pointer;

          // Fix white top line on mobile
          transform: scale(1);
          transition: none 125ms ease-in-out;
          transition-property: filter, transform;

          ${!Target.__MOBILE__
            ? css`
                &:hover {
                  filter: brightness(110%);
                }

                &:active {
                  transform: scale(0.96);
                }
              `
            : css`
                &:active {
                  filter: brightness(110%);
                  transform: scale(0.96);
                }
              `}
        `
      : css`
          pointer-events: none;
        `}

  ${({ $status, $animationDelay }) => {
    if ($status === MPCardStatuses.PENDING) {
      return css`
        &:after {
          content: '';
          position: absolute;
          width: 100%;
          height: 100%;
          top: 0;
          left: 0;
          background: transparent;
          box-shadow: 0px 0px 8px 1px white;
          opacity: 0.2;

          animation: ${haloAnim} ${MP_CARD_HALO_DURATION}ms ease-in-out ${$animationDelay}ms infinite;
        }
      `;
    }
    if ($status !== MPCardStatuses.WON) {
      return css`
        &::after {
          background-color: #000;

          opacity: 0.5;
          position: absolute;
        }
      `;
    }
  }}
`;

const DefaultFrameSvg = styled(MPCardColorSvg)`
  color: ${({ $color }) => $color};
  height: 100%;
  width: 100%;
`;

const StationFrameSvg = styled(MPCardStationSvg)`
  height: 100%;
  width: 100%;
`;

const WaterFrameSvg = styled(MPCardWaterSvg)`
  height: 100%;
  width: 100%;
`;
const ElectricityFrameSvg = styled(MPCardElectricitySvg)`
  height: 100%;
  width: 100%;
`;

const StreetName = styled.div`
  align-items: center;
  color: #000;
  display: flex;
  flex-flow: column;
  font-size: ${({ $fontSize }) => $fontSize}px;
  font-weight: bold;
  justify-content: center;
  line-height: 1em;
  position: absolute;
  text-align: center;
  text-transform: uppercase;
  top: 54%;
  white-space: pre-wrap;
  width: 100%;

  ${({ $alignmentText }) =>
    $alignmentText === 'center'
      ? css`
          height: 1.8em;
        `
      : null}
`;

const StyledPendingBorderSvg = styled(MPCardPendingBorderSvgAsComponent)`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
`;

type MPCardProps = {
  className?: string;
  familyId: MPFamilyId;
  streetName: string;
  cardType?: MPCardType;
  status?: MPCardStatus;
  animationDelay?: number;
  isClickable?: boolean;
  onClick: () => void;
};

const MPCard = ({
  className = '',
  familyId,
  streetName = '',
  cardType = MPCardTypes.COLOR,
  status = MPCardStatuses.OPEN,
  animationDelay = 0,
  isClickable,
  onClick,
}: MPCardProps) => {
  const cardRef = useRef();
  const [fontSize, setFontSize] = useState(FONT_SIZE_BASE);

  useLayoutEffect(() => {
    if (!cardRef.current) {
      return;
    }

    setFontSize(cardRef.current.offsetWidth * 0.1);
  }, [cardRef, fontSize]);

  const alignmentText = useMemo(() => (cardType === MPCardTypes.STATION ? 'top' : 'center'), [cardType]);

  const cardFrame = useMemo(() => {
    switch (cardType) {
      case MPCardTypes.STATION:
        return <StationFrameSvg />;
      case MPCardTypes.ELECTRICITY:
        return <ElectricityFrameSvg />;
      case MPCardTypes.WATER:
        return <WaterFrameSvg />;
      case MPCardTypes.COLOR:
      default:
        return <DefaultFrameSvg $color={MPColors[familyId]} />;
    }
  }, [cardType, familyId]);

  return (
    <StyleReceiver className={className} ref={cardRef} onClick={onClick}>
      <Wrapper $status={status} $animationDelay={animationDelay} $isClickable={isClickable}>
        {cardFrame}
        <StreetName $fontSize={fontSize} $alignmentText={alignmentText}>
          {streetName}
        </StreetName>
        {status === MPCardStatuses.PENDING ? <StyledPendingBorderSvg /> : null}
      </Wrapper>
    </StyleReceiver>
  );
};

export default MPCard;
